/* ============================================================
   Herbal Synergies — Custom Cart Drawer controller
   Registers the <cart-drawer> custom element (same tag Dawn's
   product-form.js looks for, so every add-to-cart source opens
   this drawer with no other changes). Thin controller: open/close,
   focus trap, cart mutations via the AJAX API, and a section
   re-render after each mutation (Liquid is the source of truth).
   No external libraries.
   ============================================================ */
(function () {
  'use strict';

  var ROUTES = window.routes || {};
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var CHANGE_URL = ROUTES.cart_change_url || ROOT.replace(/\/$/, '') + '/cart/change.js';
  var UPDATE_URL = ROUTES.cart_update_url || ROOT.replace(/\/$/, '') + '/cart/update.js';
  var CART_URL = ROUTES.cart_url || ROOT.replace(/\/$/, '') + '/cart';
  var SECTIONS = ['cart-drawer', 'cart-icon-bubble'];

  function pub(name, detail) {
    try {
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS[name]) {
        window.publish(window.PUB_SUB_EVENTS[name], detail);
      }
    } catch (e) {}
  }

  if (customElements.get('cart-drawer')) return; // avoid double-definition

  var CartDrawer = class extends HTMLElement {
    constructor() {
      super();
      this.isMutating = false;
      this.activeElement = null;
      this.trigger = null;
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.classList.add('is-ready');
      this.setHeaderIcon();
      this.bindEvents();
    }

    /* ---------- product-form.js contract ---------- */
    getSectionsToRender() {
      return [
        { id: 'cart-drawer', selector: '#CartDrawer' },
        { id: 'cart-icon-bubble', selector: '#cart-icon-bubble' },
      ];
    }

    setActiveElement(el) {
      this.activeElement = el;
    }

    renderContents(parsedState) {
      // Called by product-form after /cart/add.js. parsedState.sections holds HTML.
      if (parsedState && parsedState.sections) {
        this.applySections(parsedState.sections);
      }
      var self = this;
      // Consolidate stacked single books into the matching pack BEFORE the drawer
      // is shown, so the shopper never sees the intermediate "1 Book x 2" state.
      this.normalizeCore().then(function () {
        self.classList.remove('is-empty');
        self.open(self.activeElement);
        pub('cartUpdate', { source: 'hs-cart-drawer', cartData: parsedState });
      });
    }

    /* ---------- Header cart icon ---------- */
    setHeaderIcon() {
      var icon = document.querySelector('#cart-icon-bubble');
      if (!icon) return;
      icon.setAttribute('role', 'button');
      icon.setAttribute('aria-haspopup', 'dialog');
      icon.setAttribute('aria-expanded', 'false');
      var self = this;
      icon.addEventListener('click', function (e) {
        e.preventDefault();
        self.open(icon);
      });
    }

    /* ---------- Event wiring (delegated on the element) ---------- */
    bindEvents() {
      var self = this;

      this.addEventListener('click', function (e) {
        var closeBtn = e.target.closest('[data-cd-close]');
        if (closeBtn) { e.preventDefault(); self.close(); return; }

        var overlay = e.target.closest('#CartDrawer-Overlay');
        if (overlay) { self.close(); return; }

        var remove = e.target.closest('[data-cd-remove]');
        if (remove) { e.preventDefault(); self.onRemove(remove); return; }

        var qty = e.target.closest('[data-cd-qty]');
        if (qty) { e.preventDefault(); self.onQtyStep(qty); return; }

        var swap = e.target.closest('[data-cd-swap]');
        if (swap) { e.preventDefault(); self.onSwap(swap); return; }

        var upsell = e.target.closest('[data-cd-upsell]');
        if (upsell) { e.preventDefault(); self.onUpsell(upsell); return; }
      });

      // Quantity input (non-core) commit on change
      this.addEventListener('change', function (e) {
        var input = e.target.closest('[data-cd-qtyinput]');
        if (!input) return;
        var val = parseInt(input.value, 10);
        if (isNaN(val) || val < 0) val = 0;
        self.changeLine(input.getAttribute('data-key'), val, self.lineOf(input));
      });

      // Checkout button loading state (do not preventDefault — let it submit)
      this.addEventListener('submit', function (e) {
        var btn = self.querySelector('[data-cd-checkout]');
        if (btn && !btn.disabled) { btn.classList.add('is-loading'); btn.setAttribute('aria-busy', 'true'); }
      });
    }

    /* ---------- Open / close ---------- */
    open(trigger) {
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      document.body.classList.add('hs-cd-open');
      var icon = document.querySelector('#cart-icon-bubble');
      if (icon) icon.setAttribute('aria-expanded', 'true');
      var panel = this.querySelector('#CartDrawer');
      var self = this;
      // focus after transition start
      window.requestAnimationFrame(function () {
        var focusTarget = self.querySelector('[data-cd-close]') || panel;
        if (focusTarget) focusTarget.focus();
      });
      document.addEventListener('keydown', this.onKeydown);
    }

    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('hs-cd-open');
      var icon = document.querySelector('#cart-icon-bubble');
      if (icon) icon.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', this.onKeydown);
      var t = this.trigger;
      if (t && typeof t.focus === 'function') {
        try { t.focus(); } catch (e) {}
      }
    }

    onKeydown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); this.close(); return; }
      if (e.key !== 'Tab') return;
      var panel = this.querySelector('#CartDrawer');
      if (!panel) return;
      var focusables = panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), textarea, select, [tabindex]:not([tabindex="-1"])'
      );
      var list = Array.prototype.filter.call(focusables, function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      });
      if (!list.length) { e.preventDefault(); panel.focus(); return; }
      var first = list[0], last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    /* ---------- Mutations ---------- */
    lineOf(el) { return el.closest('[data-cd-line]'); }

    lock(line) {
      this.isMutating = true;
      if (line) line.classList.add('is-loading');
      this.clearError(line);
    }
    unlock(line) {
      this.isMutating = false;
      if (line && line.isConnected) line.classList.remove('is-loading');
    }

    clearError(line) {
      var g = this.querySelector('#hs-cd-errors'); if (g) g.textContent = '';
      if (line) { line.classList.remove('has-error'); var le = line.querySelector('.hs-cd__item-error'); if (le) le.textContent = ''; }
    }
    showError(msg, line) {
      var text = msg || 'Something went wrong. Please try again.';
      if (line && line.isConnected) {
        line.classList.add('has-error');
        var le = line.querySelector('.hs-cd__item-error');
        if (le) le.textContent = text;
      } else {
        var g = this.querySelector('#hs-cd-errors');
        if (g) g.textContent = text;
      }
    }
    announce(msg) {
      var live = this.querySelector('#hs-cd-live');
      if (live) live.textContent = msg;
    }

    postJSON(url, body) {
      body.sections = SECTIONS;
      body.sections_url = window.location.pathname;
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      }).then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) { var err = new Error(data && data.description ? data.description : 'Cart error'); err.data = data; throw err; }
          return data;
        });
      });
    }

    onRemove(btn) {
      if (this.isMutating) return;
      var line = this.lineOf(btn);
      this.changeLine(btn.getAttribute('data-key'), 0, line);
    }

    onQtyStep(btn) {
      if (this.isMutating) return;
      var line = this.lineOf(btn);
      var input = line.querySelector('[data-cd-qtyinput]');
      var cur = parseInt(input ? input.value : '1', 10) || 1;
      var next = btn.getAttribute('data-cd-qty') === 'plus' ? cur + 1 : cur - 1;
      if (next < 0) next = 0;
      this.changeLine(btn.getAttribute('data-key'), next, line);
    }

    changeLine(key, quantity, line) {
      if (this.isMutating) return;
      this.lock(line);
      var self = this;
      this.postJSON(CHANGE_URL, { id: key, quantity: quantity })
        .then(function (data) {
          self.applySections(data.sections);
          self.announce(quantity === 0 ? 'Item removed from cart.' : 'Cart updated.');
          pub('cartUpdate', { source: 'hs-cart-drawer', cartData: data });
          return self.normalizeCore();
        })
        .catch(function (err) { self.showError(err && err.message, line); })
        .then(function () { self.unlock(line); });
    }

    onSwap(btn) {
      if (this.isMutating) return;
      var line = this.lineOf(btn);
      var from = btn.getAttribute('data-from');
      var to = btn.getAttribute('data-to');
      // A package selection always resolves to exactly one unit of the chosen
      // pack. 1/2/3 books IS the quantity mechanism, so line quantity never
      // stacks (prevents a doubled 2x-price line).
      this.swapVariants([from], to, 1, line, 'Package updated.');
    }

    onUpsell(btn) {
      if (this.isMutating) return;
      btn.disabled = true;
      var add = btn.getAttribute('data-add');
      var removeIds = (btn.getAttribute('data-remove') || '').split(',').filter(Boolean);
      this.swapVariants(removeIds, add, 1, null, 'Upgraded to the Complete Herbal System.');
    }

    // Remove a set of variant lines and set a target variant to an exact
    // quantity, atomically via /cart/update.js. SET semantics (not accumulate):
    // the target resolves to exactly addQty regardless of what is already in the
    // cart, so a double-tap or a stale package button cannot stack the same pack
    // into a doubled quantity.
    swapVariants(removeIds, addId, addQty, line, successMsg) {
      this.lock(line);
      var self = this;
      var updates = {};
      removeIds.forEach(function (id) { if (id) updates[id] = 0; });
      updates[addId] = addQty;
      this.postJSON(UPDATE_URL, { updates: updates })
        .then(function (data) {
          self.applySections(data.sections);
          self.announce(successMsg || 'Cart updated.');
          pub('cartUpdate', { source: 'hs-cart-drawer', cartData: data });
        })
        .catch(function (err) { self.showError(err && err.message, line); })
        .then(function () { self.unlock(line); });
    }

    // A pack IS the quantity. If a shopper stacks single books (adding the same
    // 1-book variant more than once, or a single alongside an existing pack), fold
    // the family into the one pack variant that equals the total book count:
    // 2 books -> 2-pack, 3 books -> 3-pack. Totals of 1 need no change; totals above
    // 3 have no single-pack equivalent and are left alone (the qty stepper shows).
    // Runs off the freshly rendered DOM and only fires an update when the target
    // state differs from the current one, so it can't loop.
    normalizeCore() {
      if (this._normalizing) return Promise.resolve(false);
      var lines = Array.prototype.slice.call(this.querySelectorAll('[data-cd-line][data-cd-family]'));
      if (!lines.length) return Promise.resolve(false);

      var fam = {};
      lines.forEach(function (li) {
        var f = li.getAttribute('data-cd-family');
        if (!f) return;
        var books = parseInt(li.getAttribute('data-cd-books'), 10) || 1;
        var qty = parseInt(li.getAttribute('data-cd-qty-current'), 10) || 1;
        var vid = li.getAttribute('data-variant');
        if (!fam[f]) {
          fam[f] = {
            total: 0,
            variants: {},
            pack1: li.getAttribute('data-cd-pack1'),
            pack2: li.getAttribute('data-cd-pack2'),
            pack3: li.getAttribute('data-cd-pack3'),
          };
        }
        fam[f].total += books * qty;
        fam[f].variants[vid] = (fam[f].variants[vid] || 0) + qty;
      });

      var updates = {};
      var need = false;
      Object.keys(fam).forEach(function (f) {
        var d = fam[f];
        if (d.total < 1 || d.total > 3) return; // no single pack equals this count
        var target = d.total === 1 ? d.pack1 : d.total === 2 ? d.pack2 : d.pack3;
        if (!target) return;
        var vids = Object.keys(d.variants);
        var already = vids.length === 1 && vids[0] === target && d.variants[target] === 1;
        if (already) return;
        need = true;
        vids.forEach(function (v) { updates[v] = 0; });
        updates[target] = 1;
      });
      if (!need) return Promise.resolve(false);

      this._normalizing = true;
      var self = this;
      return this.postJSON(UPDATE_URL, { updates: updates })
        .then(function (data) {
          self.applySections(data.sections);
          pub('cartUpdate', { source: 'hs-cart-drawer', cartData: data });
          return true;
        })
        .catch(function () { return false; })
        .then(function (r) { self._normalizing = false; return r; });
    }

    /* ---------- Rendering ---------- */
    getSectionInnerHTML(html, selector) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var el = doc.querySelector(selector);
      return el ? el.innerHTML : null;
    }

    applySections(sections) {
      if (!sections) return;
      // Cart drawer body
      var drawerHtml = sections['cart-drawer'];
      if (drawerHtml) {
        var inner = this.getSectionInnerHTML(drawerHtml, '#CartDrawer');
        var target = this.querySelector('#CartDrawer');
        if (inner !== null && target) target.innerHTML = inner;
        // reflect empty state on the wrapper
        var parsedWrap = new DOMParser().parseFromString(drawerHtml, 'text/html').querySelector('cart-drawer');
        if (parsedWrap) {
          this.classList.toggle('is-empty', parsedWrap.classList.contains('is-empty'));
        }
      }
      // Cart icon bubble
      var bubbleHtml = sections['cart-icon-bubble'];
      if (bubbleHtml) {
        var iconTarget = document.querySelector('#cart-icon-bubble');
        var parsedBubble = new DOMParser().parseFromString(bubbleHtml, 'text/html');
        var newInner = parsedBubble.querySelector('#cart-icon-bubble') || parsedBubble.querySelector('.shopify-section');
        if (iconTarget && newInner) iconTarget.innerHTML = newInner.innerHTML;
      }
    }
  };

  customElements.define('cart-drawer', CartDrawer);
})();
